
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Image as ImageIcon, Calendar, User, Settings, Home } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SharedImageData {
  id: string;
  prompt: string;
  image_url: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  created_at: string;
  profile_email: string;
  profile_name: string;
  shared_at: string;
  shared_by_admin_email: string;
  message?: string;
}

const SharedImage = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [imageData, setImageData] = useState<SharedImageData | null>(null);
  const [shareInfo, setShareInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (shareToken) {
      fetchSharedImage();
    }
  }, [shareToken]);

  const fetchSharedImage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Securely fetch shared image via RPC (handles expiry and access count)
      const { data, error: rpcError } = await supabase
        .rpc('get_shared_image_by_token', { share_token: shareToken });

      if (rpcError) {
        throw rpcError;
      }

      if (!data) {
        setError('Image not found or no longer shared');
        return;
      }

      // RPC returns a JSON object with secure image data (admin emails masked for non-admins)
      setImageData(data as unknown as SharedImageData);
      // Preserve message display without exposing the shares table directly
      setShareInfo((data as any).message ? { message: (data as any).message } : null);

    } catch (error) {
      console.error('Error fetching shared image:', error);
      setError('Failed to load shared image');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = async () => {
    if (!imageData) return;

    try {
      const response = await fetch(imageData.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shared-image-${imageData.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const maskEmail = (email?: string) => {
    if (!email) return '';
    const [user, domain] = email.split('@');
    if (!user || !domain) return email;
    const visible = user.slice(0, 3);
    return `${visible}***@${domain}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
        </div>

        <div className="container mx-auto px-6 py-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardContent className="p-12">
                <div className="text-center text-gray-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                  <p className="text-white text-lg">Loading shared image...</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !imageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
        </div>

        <header className="bg-black/20 backdrop-blur-md border-b border-white/10 relative z-10">
          <div className="container mx-auto px-6 py-4">
            <nav className="flex items-center justify-between">
              <RouterLink to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">BG</span>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  BackgroundGenerator
                </span>
              </RouterLink>
              
              <RouterLink to="/">
                <Button variant="ghost" className="flex items-center text-white hover:bg-white/10">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </RouterLink>
            </nav>
          </div>
        </header>

        <div className="container mx-auto px-6 py-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardContent className="p-12">
                <div className="text-center text-gray-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-white mb-2">Image Not Found</h1>
                  <p className="text-gray-300 mb-6">
                    {error || 'The shared image you are looking for does not exist or is no longer available.'}
                  </p>
                  <RouterLink to="/">
                    <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      <Home className="w-4 h-4 mr-2" />
                      Go Home
                    </Button>
                  </RouterLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>

      <header className="bg-black/20 backdrop-blur-md border-b border-white/10 relative z-10">
        <div className="container mx-auto px-6 py-4">
          <nav className="flex items-center justify-between">
            <RouterLink to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">BG</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                BackgroundGenerator
              </span>
            </RouterLink>
            
            <RouterLink to="/">
              <Button variant="ghost" className="flex items-center text-white hover:bg-white/10 border border-white/20">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </RouterLink>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Shared Image
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
              AI Generated Image
            </h1>
            <p className="text-gray-300">
              Shared by {maskEmail(imageData.shared_by_admin_email)} on {formatDate(imageData.shared_at)}
            </p>
          </div>

          {shareInfo?.message && (
            <Card className="bg-blue-600/10 backdrop-blur-md border-blue-500/30 mb-8">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">Personal Message</h3>
                <p className="text-blue-200">{shareInfo.message}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Display */}
            <div className="lg:col-span-2">
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-6">
                  <div className="relative">
                    <img
                      src={imageData.image_url}
                      alt={imageData.prompt}
                      className="w-full rounded-lg shadow-2xl"
                    />
                    <Button
                      onClick={downloadImage}
                      className="absolute bottom-4 right-4 bg-black/50 hover:bg-black/70 backdrop-blur-sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Image Details */}
            <div className="space-y-6">
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Settings className="w-5 h-5 mr-2" />
                    Generation Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400">Prompt</label>
                    <p className="text-white">{imageData.prompt}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400">Model</label>
                      <p className="text-white text-sm">{imageData.model}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Size</label>
                      <p className="text-white text-sm">{imageData.size}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Quality</label>
                      <p className="text-white text-sm">{imageData.quality}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Style</label>
                      <p className="text-white text-sm">{imageData.style}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Created
                    </label>
                    <p className="text-white text-sm">{formatDate(imageData.created_at)}</p>
                  </div>

                  {imageData.profile_email && (
                    <div>
                      <label className="text-sm text-gray-400 flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        Generated by
                      </label>
                      <p className="text-white text-sm">
                        {imageData.profile_name || maskEmail(imageData.profile_email)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-6">
                  <Button
                    onClick={downloadImage}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download High Quality
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedImage;
